import type { GroupTurnMetadata } from '@crowdex/groups';
import { ChevronRight, Users } from 'lucide-react';
import { useId, useState } from 'react';

/**
 * Where a group gave a member's conversation its turn: "Group · 2 new messages", in place of the
 * prompt its agent was told (protocol, never the user's words). Opened, the plain messages it
 * heard, as the group said them.
 */
export function GroupTurnMarker({
  turn,
}: {
  turn: Extract<GroupTurnMetadata, { kind: 'prompt' }>;
}) {
  const [open, setOpen] = useState(false);
  const listId = useId();
  const count = turn.messages.length;
  return (
    <section aria-label="Group turn" className="flex w-full flex-col gap-2" data-group-turn>
      <div className="type-meta flex items-center gap-3 text-muted-foreground">
        <span aria-hidden className="h-px flex-1 bg-hairline" />
        <button
          aria-controls={listId}
          aria-expanded={open}
          className="flex items-center gap-1.5 rounded-control px-2 py-0.5 hover:bg-muted hover:text-foreground"
          disabled={count === 0}
          onClick={() => setOpen((value) => !value)}
          type="button"
        >
          <Users aria-hidden className="size-3" />
          Group · {count === 1 ? '1 new message' : `${count} new messages`}
          {count > 0 && (
            <ChevronRight
              aria-hidden
              className={`size-3 transition-transform ${open ? 'rotate-90' : ''}`}
            />
          )}
        </button>
        <span aria-hidden className="h-px flex-1 bg-hairline" />
      </div>
      {open && (
        <ul className="flex flex-col gap-1 text-muted-foreground text-sm" id={listId}>
          {turn.messages.map((message, index) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: The messages a turn heard never change: their place is their identity.
            <li className="whitespace-pre-wrap break-words" key={index}>
              <span className="font-semibold text-foreground">
                {message.authorId === 'user' ? 'You' : message.authorName}
              </span>
              {`: ${message.text}`}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
